import { ChevronDown16, ChevronRight16 } from '@carbon/icons-react';
import moment from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { StatusField, StatusGroup } from 'api/tasks';
import {
  CpBody01Primary,
  CpBody02Secondary,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { humanise } from 'components/atoms/humanised-date';
import { Color } from 'components/colors';
import { SingleLineEllipsis } from 'components/styles';
import { useProjectPhaseStatusField } from 'data/fields/hooks';
import { useTranslate } from 'hooks/use-translation';
import { ArrElement } from 'utils/arrays';
import { useGetBackground, useGetStyle } from '../types/utils';
import { CpWidgetProps } from '../types/widget';
import { CpSectionWrapper, PaddingSize } from './common';
import { CpWidgetHeader } from './common/header';

interface CpProjectOverviewData extends Record<string, unknown> {
  title: string;
  description?: string;
  status: {
    label: string;
    value: number;
    color?: Color;
  };
  projectPhases: {
    projectPhaseName: string;
    projectPhaseId: number;
    inProgressTaskCount: number;
    totalTaskCount: number;
    completedTaskCount: number;
    startDate: string;
    dueDate: string;
  }[];
  completionPercentage: number;
  inProgressPercentage: number;
  startDate: string;
  endDate: string;
}

enum PhaseStatusType {
  Completed = 'completed',
  Overdue = 'became_overdue',
  Upcoming = 'upcoming',
  InProgress = 'in_progress',
}
const phaseStatusGroupSortOrder = [
  PhaseStatusType.InProgress,
  PhaseStatusType.Upcoming,
  PhaseStatusType.Completed,
  PhaseStatusType.Overdue,
];
const isPhaseMatchesFilter = (
  statusType: PhaseStatusType,
  phase: Record<string, unknown>,
  statusField?: StatusField,
) => {
  const phaseStatus = ((phase?.fields ?? []) as any[])?.find(
    (field) =>
      field.fieldId === statusField?.fieldId || field.fieldName === 'Status',
  );

  if (phaseStatus == null) {
    return false;
  }

  switch (statusType) {
    case PhaseStatusType.Completed:
      return phaseStatus.metaFieldValue?.group === StatusGroup.Completed;
    case PhaseStatusType.Overdue:
      return (
        phaseStatus.metaFieldValue?.group !== StatusGroup.Completed &&
        moment(phase.dueDate as string).isBefore(moment())
      );
    case PhaseStatusType.Upcoming:
      return (
        phaseStatus.metaFieldValue?.group !== StatusGroup.Completed &&
        moment(phase.dueDate as string).isAfter(moment())
      );
    case PhaseStatusType.InProgress:
      return phaseStatus.metaFieldValue?.group === StatusGroup.InProgress;
    default:
      return false;
  }
};
const ProgressBar = styled.div`
  width: 100%;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: start;
  border-radius: var(--border-radius-default);
  overflow: hidden;
  background-color: var(--cp-background-as-white-opacity);
  .cp-light-background &,
  .cp-very-light-background & {
    background-color: var(--cp-background-as-black-opacity);
  }
  position: relative;
`;
const ProgressBarWrapper = styled.div`
  display: flex;
  justify-content: center;
  ${ProgressBar} {
    max-width: 200px;
  }
`;

const CompletedProgressBar = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => `${percentage}%`};
  height: 100%;
  background-color: var(--cp-green-40);
`;

const InProgressProgressBar = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => `${percentage}%`};
  height: 100%;
  background-color: var(--cp-green-20);
`;

const PhaseValue = styled(CpBody01Primary)`
  ${SingleLineEllipsis};
`;
const PhaseRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-05);
  ${PhaseValue}, ${ProgressBarWrapper} {
    flex: 1;
  }
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--cp-spacing-03);
    ${ProgressBarWrapper} {
      width: 100%;
    }
    ${ProgressBar} {
      max-width: unset;
    }
  }
`;
const PhaseListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-05);

  @media (max-width: 480px) {
    gap: var(--cp-spacing-07);
  }
`;
const GroupedPhasesHeader = styled(CpBody02Secondary)`
  padding: var(--cp-spacing-06) 0;
  display: flex;
  align-items: center;
  gap: var(--cp-spacing-02);
  cursor: pointer;
`;
const GroupedPhases = styled.div`
  border-top: 1px solid var(--cp-border-subtle-auto-adjust);
  ${PhaseListWrapper} {
    padding: var(--cp-spacing-05) var(--cp-spacing-08) var(--cp-spacing-07);
  }
  @media (max-width: 480px) {
    ${PhaseListWrapper} {
      padding: var(--cp-spacing-05) var(--cp-spacing-06) var(--cp-spacing-07);
    }
  }
`;
const PhasesWrapper = styled.div`
  margin-top: var(--cp-spacing-10);
`;

const formatDates = (startDate?: string, dueDate?: string) => {
  if (!startDate && !dueDate) {
    return '-';
  }

  if (!startDate) {
    return humanise(moment(dueDate));
  }

  if (!dueDate) {
    return humanise(moment(startDate));
  }

  return `${humanise(moment(startDate))} - ${humanise(moment(dueDate))}`;
};

const Phase: React.FC<ArrElement<CpProjectOverviewData['projectPhases']>> = ({
  projectPhaseName,
  inProgressTaskCount,
  totalTaskCount,
  completedTaskCount,
  startDate,
  dueDate,
}) => {
  const completedPercentage = useMemo(() => {
    if (!totalTaskCount) {
      return 0;
    }

    return (completedTaskCount / totalTaskCount) * 100;
  }, [completedTaskCount, totalTaskCount]);

  const inProgressPercentage = useMemo(() => {
    if (!totalTaskCount) {
      return 0;
    }

    return (inProgressTaskCount / totalTaskCount) * 100;
  }, [inProgressTaskCount, totalTaskCount]);
  const formattedDates = useMemo(
    () => formatDates(startDate, dueDate),
    [dueDate, startDate],
  );

  return (
    <PhaseRow data-cy={`cp.project-overview.phase.${projectPhaseName}`}>
      <PhaseValue autoAdjustColor>{projectPhaseName}</PhaseValue>
      <ProgressBarWrapper>
        <ProgressBar data-cy="cp.project-overview.phase.progress-bar">
          <CompletedProgressBar
            data-cy="cp.project-overview.phase.progress-bar.completed"
            percentage={completedPercentage}
          />
          <InProgressProgressBar
            percentage={inProgressPercentage}
            data-cy="cp.project-overview.phase.progress-bar.in_progress"
          />
        </ProgressBar>
      </ProgressBarWrapper>
      <PhaseValue autoAdjustColor>{formattedDates}</PhaseValue>
    </PhaseRow>
  );
};

interface Props {
  phases: CpProjectOverviewData['projectPhases'];
  statusType: PhaseStatusType;
  expandByDefault: boolean;
}
const Phases: FC<Props> = ({ phases, statusType, expandByDefault }) => {
  const [isExpanded, setIsExpanded] = useState(expandByDefault);
  const translate = useTranslate();

  return (
    <GroupedPhases>
      <GroupedPhasesHeader
        autoAdjustColor
        onClick={() => setIsExpanded((state) => !state)}
      >
        {isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
        <FormattedMessage
          id="customer_portal.widgets.project_phase_with_status"
          values={{
            count: phases?.length,
            statusName: translate(
              `customer_portal.widgets.project_phase.statuses.${statusType}`,
            ),
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </GroupedPhasesHeader>
      {isExpanded ? (
        <PhaseListWrapper>
          {phases?.map((phase) => (
            <Phase key={phase.projectPhaseId} {...phase} />
          ))}
        </PhaseListWrapper>
      ) : null}
    </GroupedPhases>
  );
};

const KeyInformationTitle = styled(CpBody02Secondary)``;
const KeyInformationValue = styled(CpHeading03Primary)<{ color?: Color }>`
  ${(props) =>
    props?.color
      ? css`
          color: var(--${props.color}-60);
        `
      : css``};
  ${SingleLineEllipsis};
`;
const CompletionValue = styled(KeyInformationValue)`
  display: flex;
  align-items: center;
  gap: var(--cp-spacing-05);
`;
const KeyInformation = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--cp-spacing-05);
  width: 100%;
`;
const CompletionBlock = styled(KeyInformation)`
  width: 240px;
  max-width: 100%;
  flex: unset;
  @media (max-width: 480px) {
    width: 100%;
  }
`;
const KeyInformationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--cp-spacing-09);
  padding-top: var(--spacing-06);
  @media (max-width: 480px) {
    flex-direction: column;
    gap: var(--cp-spacing-07);
  }
`;

export const CpProjectOverView: React.FC<CpWidgetProps> = ({
  widget,
  disabled,
  data: _data,
}) => {
  const bg = useGetBackground(widget);
  const paddingY = useGetStyle(widget, 'paddingY', PaddingSize.Medium);
  const data = useMemo(() => _data as CpProjectOverviewData, [_data]);

  const formattedProjectDates = useMemo(
    () => formatDates(data.startDate, data.endDate),
    [data.startDate, data.endDate],
  );

  const completionPercentage = useMemo(() => {
    if (!data.completionPercentage) {
      return '-';
    }

    return Math.round(data.completionPercentage);
  }, [data.completionPercentage]);
  const statusField = useProjectPhaseStatusField();
  const groupedPhases = useMemo(
    () =>
      phaseStatusGroupSortOrder.reduce((groupedByStatusType, statusType) => {
        return {
          ...groupedByStatusType,
          [statusType]: data.projectPhases?.filter((phase) =>
            isPhaseMatchesFilter(statusType, phase, statusField),
          ),
        };
      }, {}),
    [data.projectPhases, statusField],
  );
  const defaultExpandedGroup = useMemo(
    () =>
      phaseStatusGroupSortOrder.find(
        (statusType) => groupedPhases[statusType]?.length > 0,
      ),
    [groupedPhases],
  );

  return (
    <CpSectionWrapper background={bg} paddingY={paddingY} isDisabled={disabled}>
      <CpWidgetHeader>
        <CpWidgetHeader.Title
          data-cy="cp.project-overview.title"
          autoAdjustColor
        >
          {data?.title}
        </CpWidgetHeader.Title>
        <CpWidgetHeader.Description
          data-cy="cp.project-overview.description"
          autoAdjustColor
        >
          {data?.description}
        </CpWidgetHeader.Description>
      </CpWidgetHeader>
      <KeyInformationWrapper>
        {/* <KeyInformation data-cy="cp.project-overview.status_card">
          <KeyInformationTitle autoAdjustColor>
            <FormattedMessage id="status" />
          </KeyInformationTitle>
          <KeyInformationValue
            data-cy="cp.project-overview.status.value"
            color={data?.status?.color}
          >
            {data?.status?.label ?? '-'}
          </KeyInformationValue>
        </KeyInformation> */}

        <CompletionBlock data-cy="cp.project-overview.summary-info-card.completion">
          <KeyInformationTitle autoAdjustColor>
            <FormattedMessage id="completion" />
          </KeyInformationTitle>
          <CompletionValue
            autoAdjustColor
            data-cy="cp.project-overview.summary-info-card.completion-value"
          >
            {completionPercentage && completionPercentage !== '-'
              ? `${completionPercentage}%`
              : '-'}
            {data?.completionPercentage ? (
              <ProgressBar data-cy="cp.project-overview.summary-info-card.progress_bar">
                <CompletedProgressBar
                  data-cy="cp.project-overview.summary-info-card.progress_bar.completed"
                  percentage={data?.completionPercentage}
                />
                <InProgressProgressBar
                  data-cy="cp.project-overview.summary-info-card.progress_bar.in_progress"
                  percentage={data?.inProgressPercentage}
                />
              </ProgressBar>
            ) : (
              <></>
            )}
          </CompletionValue>
        </CompletionBlock>
        <KeyInformation data-cy="cp.project-overview.summary-info-card.project-dates">
          <KeyInformationTitle autoAdjustColor>
            <FormattedMessage id="project_dates" />
          </KeyInformationTitle>
          <KeyInformationValue
            autoAdjustColor
            data-cy="cp.project-overview.summary-info-card.project-dates-value"
          >
            {formattedProjectDates}
          </KeyInformationValue>
        </KeyInformation>
      </KeyInformationWrapper>

      {(data.projectPhases?.length ?? 0) !== 0 ? (
        <PhasesWrapper>
          {phaseStatusGroupSortOrder.map((statusType) =>
            groupedPhases[statusType]?.length > 0 ? (
              <Phases
                expandByDefault={defaultExpandedGroup === statusType}
                key={statusType}
                phases={groupedPhases[statusType]}
                statusType={statusType}
              />
            ) : (
              <></>
            ),
          )}
        </PhasesWrapper>
      ) : null}
    </CpSectionWrapper>
  );
};
